import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CTASection, SectionHeading } from "@/components/sections";
import { Stagger, StaggerItem } from "@/components/primitives";
import { ProductHero } from "@/components/product-parts";
import { Link } from "@/components/LangLink";
import { ImageIcon, ArrowUpRight, Star } from "lucide-react";
import { RalMark, RAL_TEXT } from "@/components/quality";
import { SCHIEBLER_PRODUCTS, SCHIEBLER_CATEGORIES, schieblerByCategory } from "@/lib/schiebler";

const Placeholder = ({ ratio = "aspect-[4/3]", testid }) => (
  <div className={`relative ${ratio} w-full overflow-hidden rounded-xl bg-cloud border border-[#E7EDF2]`} data-testid={testid}>
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-navy/25">
      <ImageIcon size={26} />
      <span className="text-[10px] font-semibold uppercase tracking-wider">Foto in arrivo</span>
    </div>
  </div>
);

const CardImage = ({ p, ratio }) =>
  p.cover ? (
    <div className={`relative ${ratio} w-full overflow-hidden rounded-xl bg-cloud border border-[#E7EDF2]`} data-testid={`schiebler-cover-${p.slug}`}>
      <img src={p.cover} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    </div>
  ) : (
    <Placeholder ratio={ratio} />
  );

const FeaturedCard = ({ p }) => (
  <StaggerItem className="h-full">
    <Link
      to={`/prodotti/schiebler/${p.slug}`}
      data-testid={`schiebler-featured-${p.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-green hover:shadow-[0_28px_56px_-20px_rgba(15,25,35,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
    >
      <div className="relative">
        <CardImage p={p} ratio="aspect-[4/3]" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-green px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
          <Star size={10} className="fill-white" /> Tra i più scelti
        </span>
      </div>
      <div className="mt-5 flex flex-1 flex-col">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">{p.category}</span>
        <h3 className="mt-1.5 font-serif text-2xl font-bold text-navy">{p.name}</h3>
        <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{p.title}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green transition-all group-hover:gap-3">
          Scopri il prodotto <ArrowUpRight size={15} />
        </span>
      </div>
    </Link>
  </StaggerItem>
);

const CompactCard = ({ p }) => (
  <StaggerItem className="h-full">
    <Link
      to={`/prodotti/schiebler/${p.slug}`}
      data-testid={`schiebler-item-${p.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white p-4 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-green hover:shadow-[0_20px_44px_-20px_rgba(15,25,35,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
    >
      <div className="relative">
        <CardImage p={p} ratio="aspect-[16/10]" />
        {p.featured && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-green/90 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
            <Star size={9} className="fill-white" /> Tra i più scelti
          </span>
        )}
      </div>
      <div className="mt-4 flex flex-1 flex-col">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-green-deep">{p.category}</span>
        <h3 className="mt-1 font-serif text-lg font-bold text-navy">{p.name}</h3>
        <p className="mt-1.5 flex-1 text-xs text-muted-foreground leading-relaxed">{p.title}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-green">
          Scopri il prodotto <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </Link>
  </StaggerItem>
);

const Schiebler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const featured = SCHIEBLER_PRODUCTS.filter((p) => p.featured);
  const categories = schieblerByCategory();

  const raw = searchParams.get("cat") || "tutti";
  const active = SCHIEBLER_CATEGORIES.some((c) => c.id === raw) ? raw : "tutti";
  const visible = useMemo(
    () => (active === "tutti" ? categories : categories.filter((c) => c.id === active)),
    [active, categories]
  );

  const selectCategory = (id) => {
    if (id === "tutti") setSearchParams({});
    else setSearchParams({ cat: id });
    requestAnimationFrame(() => {
      const el = document.getElementById("schiebler-gamma");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const chips = [{ id: "tutti", label: "Tutti i prodotti" }, ...SCHIEBLER_CATEGORIES.map((c) => ({ id: c.id, label: c.label }))];

  const heroLeftExtra = (
    <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/75">
      Medisistem è distributore esclusivo per l&apos;Italia dei prodotti SCHIEBLER, storico marchio tedesco riconosciuto a livello internazionale per l&apos;eccellenza nella terapia elasto-compressiva. Grazie a questa partnership garantiamo accesso diretto ai prodotti, competenza tecnica e consulenza qualificata nella scelta della soluzione più adatta.
    </p>
  );

  const heroRight = (
    <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-5 backdrop-blur" data-testid="schiebler-hero-ral">
      <span className="inline-flex items-center rounded-xl bg-white px-3 py-2 shadow-[0_12px_32px_-14px_rgba(0,0,0,0.55)]">
        <RalMark className="h-9 w-auto" />
      </span>
      <h2 className="mt-4 font-serif text-lg lg:text-xl font-bold text-white">Qualità dei dispositivi compressivi medicali</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-white/65">{RAL_TEXT}</p>
    </div>
  );

  return (
    <>
      <ProductHero
        theme="navy"
        badge="Distributore Esclusivo per l'Italia"
        ral
        name="SCHIEBLER"
        logo="/brands/schiebler.png"
        layout="split"
        leftExtra={heroLeftExtra}
        rightContent={heroRight}
        testid="schiebler-hero"
      />

      {/* In evidenza — 3 principali (subito dopo l'hero) */}
      <section className="pt-14 lg:pt-20 pb-20 lg:pb-24 bg-cloud" data-testid="schiebler-principali">
        <div className="container-x">
          <SectionHeading
            size="cat"
            eyebrow="In evidenza"
            title="I tre prodotti più scelti."
            intro="Le tre linee SCHIEBLER più richieste. Clicca su un prodotto per aprire la scheda completa con caratteristiche, materiali, indicazioni e configurazioni."
          />
          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {featured.map((p) => <FeaturedCard key={p.slug} p={p} />)}
          </Stagger>
        </div>
      </section>

      {/* Gamma completa per macro-categoria */}
      <section className="py-20 lg:py-28 bg-white" data-testid="schiebler-range">
        <div className="container-x">
          <SectionHeading
            size="cat"
            eyebrow="Gamma completa"
            title="L'intera collezione SCHIEBLER."
            intro="Filtra per categoria per trovare più rapidamente il prodotto giusto, oppure sfoglia l'intera collezione."
          />

          {/* Filtri categoria */}
          <div className="mt-8 flex flex-wrap gap-2.5" role="group" aria-label="Filtra per categoria" data-testid="schiebler-filters">
            {chips.map((c) => {
              const on = c.id === active;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectCategory(c.id)}
                  aria-pressed={on}
                  data-testid={`schiebler-filter-${c.id}`}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${
                    on ? "border-green bg-green text-white" : "border-[#E7EDF2] text-navy/70 hover:border-green hover:text-green"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div id="schiebler-gamma" className="mt-12 space-y-14 scroll-mt-28">
            {visible.map((cat) => (
              <div key={cat.id} data-testid={`schiebler-cat-${cat.id}`}>
                <SectionHeading size="sm" maxW="max-w-2xl" eyebrow="Categoria" title={cat.label} />
                <Stagger className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {cat.products.map((p) => <CompactCard key={p.slug} p={p} />)}
                </Stagger>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Schiebler;
